import {
	ContextMenu,
	ContextMenuItem,
	FormattedCurrency,
	FormattedDateTime,
	IDataListColumn,
	PaginatedAutoDataList,
	laravelPaginatedListFetchConfig,
	useIsAddonEnabled,
	useOpen,
} from "@dgs/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { CostDrawer } from "../events/eventDetails/eventContacts/CostDrawer";
import { archivedEventService } from "../shared/api/event/archivedEventService";
import { eventService } from "../shared/api/event/eventService";
import { createEventContactService } from "../shared/api/eventContactService";
import { ContactEventIndexResource } from "../shared/types";

export const ContactEventList: FC = () => {
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");
	const { t } = useTranslation();
	const { id = "" } = useParams();
	const navigate = useNavigate();
	const eventContactService = useMemo(() => createEventContactService(id), [id]);
	const costDrawer = useOpen();
	const [costDrawerEvent, setCostDrawerEvent] = useState<ContactEventIndexResource | null>(null);
	const columns: IDataListColumn<ContactEventIndexResource>[] = useMemo(() => {
		const actionColumns: IDataListColumn<ContactEventIndexResource>[] = isPressModuleEnabled
			? [
					{
						heading: "",
						type: "actions",
						size: "max-content",
						action: (event) => (
							<ContextMenu displayShortcutsAs="icon">
								{isPressModuleEnabled && (
									<ContextMenuItem
										action={() => {
											setCostDrawerEvent(event);
											costDrawer.handleOpen();
										}}
										shortcut={true}
										title={t("Update cost")}
										label={t("Update cost")}
										icon="edit"
									/>
								)}
							</ContextMenu>
						),
					},
				]
			: [];

		const pressModuleColumns: IDataListColumn<ContactEventIndexResource>[] = isPressModuleEnabled
			? [
					{
						heading: t("Cost"),
						type: "value",
						size: "max-content",
						valueKey: "cost",
						render: (eventContact) => <FormattedCurrency value={eventContact.cost} />,
					},
					{
						heading: t("Clippings"),
						type: "value",
						size: "max-content",
						valueKey: "numberOfClippings",
					},
				]
			: [];

		return [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Start date"),
				valueKey: "start",
				type: "value",
				render: (value) => <FormattedDateTime dateTime={value.start} />,
			},
			{
				heading: t("End date"),
				valueKey: "end",
				type: "value",
				render: (value) => <FormattedDateTime dateTime={value.end} />,
			},
			{
				heading: t("Status"),
				type: "value",
				size: "max-content",
				valueKey: "status",
				render: (value) => (value.status ? t(value.status) : "-"),
			},
			...pressModuleColumns,
			{
				heading: t("Archived"),
				valueKey: "isArchived",
				type: "value",
				render: (value) => (value.isArchived ? t("Yes") : t("No")),
			},
			...actionColumns,
		];
	}, [costDrawer, isPressModuleEnabled, t]);

	return (
		<>
			<PaginatedAutoDataList
				name="contactEvents"
				heading={t("Events")}
				empty={t("No events available")}
				columns={columns}
				listFetchConfig={laravelPaginatedListFetchConfig(eventContactService.listKey, eventContactService.index)}
				initialSearch={{
					search: "",
					searchFields: [{ name: "event_translations.name", label: t("Event name") }],
				}}
				onRowClick={({ id, isArchived }) => navigate(`/events/${isArchived ? "archived" : "active"}/${id}`)}
			/>
			{costDrawerEvent && (
				<CostDrawer
					eventId={costDrawerEvent.id}
					eventContactService={costDrawerEvent.isArchived ? archivedEventService : eventService}
					open={costDrawer.open}
					contactId={id}
					cost={costDrawerEvent.cost}
					onClose={costDrawer.handleClose}
					onClosed={() => setCostDrawerEvent(null)}
				/>
			)}
		</>
	);
};
